import React, { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useInRouterContext, useLocation, useNavigate } from 'react-router-dom';
import { navigate as adapterNavigate } from '../../../lib/routerAdapter';

import {
  StyledBadge,
  StyledTabItem,
  TabContent,
  TabItems,
  Wrapper,
} from './Tabs.elements';
import { TabItem } from './TabItem';
import type { TabItemProps, TabKey } from './TabItem';
import { hasExactMatch } from './lib';

export type TabsProps = {
  /**
   * Ключ открытой по-умолчанию вкладки
   */
  defaultTab?: number | string;
  children: ReactNode;
  rightElement?: ReactNode;
  /**
   * Функция, вызываемая при смене вкладки
   */
  onChange?: (tabKey?: number | string) => any;
  /**
   * Фон у вкладок
   */
  background?: string;
  className?: string;
  type?: 'buttons' | 'primary';
  /**
   * Принудительный перерендер содержимого вкладок
   */
  forceRender?: boolean;
  /**
   * Синхронизирует tabKey и последнюю часть адреса в пути страницы
   *
   * Например, если tabKey=month, а адрес страницы /daily-budget/month
   * то такой таб будет выделяться как активный
   */
  withRouter?: boolean;
};

type TabsLocation = {
  pathname: string;
};

type TabsNavigate = (to: string) => void | Promise<void>;

type TabsContentProps = TabsProps & {
  location: TabsLocation;
  navigate: TabsNavigate;
};

type TabItemData = TabItemProps & {
  active: boolean;
};

type TabContentData = {
  tabKey?: TabKey;
  url?: string;
  children?: ReactNode;
  active: boolean;
};

/**
 * Компонент вкладок
 */
const TabsContent = ({
  defaultTab,
  children,
  background,
  className,
  type = 'primary',
  forceRender,
  rightElement,
  onChange,
  withRouter,
  location,
  navigate,
}: TabsContentProps) => {
  const exactMatch = withRouter && hasExactMatch(children);
  const [currentTab, setCurrentTab] = useState<TabKey | undefined>(
    // Когда используем синхронизацию с адресом,
    // то как ключ берем pathname из адреса
    withRouter ? location.pathname : defaultTab
  );

  useEffect(() => setCurrentTab(defaultTab), [defaultTab]);

  const changeTab = (key: TabKey | undefined) => {
    setCurrentTab(key);
    if (onChange) {
      onChange(key);
    }
  };
  /**
   * Функция для разбора вкладок
   */
  const parseChildrens = (children: ReactNode) => {
    const tabItems: TabItemData[] = [];
    const tabContents: TabContentData[] = [];
    React.Children.forEach(children, (child, index) => {
      if (React.isValidElement<TabItemProps>(child) && child.type === TabItem) {
        const {
          label,
          badge,
          children,
          onClick,
          tabKey,
          url,
          activeUrl,
        } = child.props;
        let active = false;
        if (withRouter) {
          // Если в списке табов есть полное совпадение url с pathname
          if (exactMatch) {
            if (Array.isArray(activeUrl)) {
              active = activeUrl.includes(location.pathname);
            } else {
              active = location.pathname === url;
            }
          } else {
            if (Array.isArray(activeUrl)) {
              active = activeUrl.includes(location.pathname);
            } else {
              active = url ? location.pathname.includes(url) : false;
            }
          }
        } else {
          // Если не указали таб по-умолчанию, то делаем активным самый первый
          active = currentTab ? tabKey === currentTab : index === 0;
        }
        tabItems.push({
          tabKey,
          label,
          badge,
          active,
          onClick,
          url,
        });
        tabContents.push({
          tabKey,
          url,
          children,
          active,
        });
      }
    });
    return {
      tabItems,
      tabContents,
    };
  };

  const { tabContents, tabItems } = parseChildrens(children);
  const contents = forceRender
    ? tabContents.filter((content) => content.active)
    : tabContents;
  return (
    <Wrapper className={className} type={type}>
      <TabItems background={background} type={type}>
        {tabItems.map((tabItem) => {
          const { active, tabKey, onClick, badge, label, url } = tabItem;
          const key = withRouter ? url : tabKey;
          return (
            <>
              <StyledTabItem
                active={active}
                tabKey={key}
                type={type}
                key={key}
                onClick={() => {
                  if (!active) {
                    onClick && key !== undefined && onClick(key);
                    changeTab(key);
                    withRouter && url && navigate(url);
                  }
                }}
              >
                {label}
                {badge && <StyledBadge>{badge}</StyledBadge>}
              </StyledTabItem>
            </>
          );
        })}
        {rightElement && (
          <div style={{ marginLeft: 'auto' }}>{rightElement}</div>
        )}
      </TabItems>
      {contents.map((content) => (
        <TabContent
          path={content.tabKey || content.url}
          key={content.tabKey || content.url}
          active={content.active}
        >
          {content.children}
        </TabContent>
      ))}
    </Wrapper>
  );
};

const RouterTabs = (props: TabsProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  return <TabsContent {...props} location={location} navigate={navigate} />;
};

const Tabs = (props: TabsProps) => {
  const inRouter = useInRouterContext();

  return inRouter ? (
    <RouterTabs {...props} />
  ) : (
    <TabsContent
      {...props}
      location={window.location}
      navigate={adapterNavigate}
    />
  );
};

export default Tabs;
